import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/Card'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import { IconButton } from 'components/IconButton'
import Button from 'components/Button'
import ConfirmSheet from 'components/ConfirmSheet'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
} from 'components/ModalSheet'
import JobViewModel from '../JobViewModel'
import { JobCardEmptyState } from '../JobCardEmptyState'
import { SectionError, SectionLoading } from '../Section/Section'
import JobNoteItem from './JobNoteItem'
import styles from './JobNotesCard.sass'

interface Props {
  vm: JobViewModel
}

export default observer(function JobNotesCard({ vm }: Props) {
  const notesVM = vm.jobNotesVM
  const { scroller } = notesVM

  const cardRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (notesVM.isHighlighted) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [notesVM.isHighlighted])

  return (
    <>
      <div
        ref={cardRef}
        className={cx(
          styles.cardWrap,
          notesVM.isHighlighted && styles.highlighted,
        )}
      >
        <Card>
          <CardHeader bordered compact>
            <div className={styles.cardHeader}>
              <div className={styles.titleGroup}>
                <CardTitle dataTest="job-notes-title">Job notes</CardTitle>
                <Tooltip content="Notes can be seen by anyone with access to the job, minus the client.">
                  <span className={styles.info}>
                    <Icon name="info" />
                  </span>
                </Tooltip>
              </div>
              {notesVM.canWrite && (
                <IconButton
                  onClick={() => notesVM.addNoteVM.show()}
                  aria-label="Add note"
                  dataTest="job-notes-add"
                >
                  <Icon name="add" />
                </IconButton>
              )}
            </div>
          </CardHeader>

          <CardContent listContent>
            {scroller.fetch.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading notes." error={err} />
              ),
              resolved: () =>
                notesVM.notes.length ? (
                  <div className={styles.list}>
                    {notesVM.notes.map((note) => (
                      <JobNoteItem key={note.id} vm={notesVM} note={note} />
                    ))}
                    <InfiniteScrollLoader compact scrollProvider={scroller} />
                  </div>
                ) : (
                  <JobCardEmptyState
                    topPadding
                    helpText="No notes on this job yet."
                  />
                ),
            })}
          </CardContent>
        </Card>
      </div>

      <ModalSheet
        showing={notesVM.movedNotice.showing}
        onRequestClose={notesVM.movedNotice.close}
      >
        <ModalSheetHeader withCloseButton />
        <ModalSheetContent>
          <ModalSheetContentBlock>
            <ModalSheetContentTitle>
              Notes now have their own section
            </ModalSheetContentTitle>
            <ModalSheetContentDescription>
              Your notes live in this card now. Anyone with access to the job
              can see them here, excluding the client.
            </ModalSheetContentDescription>
          </ModalSheetContentBlock>
        </ModalSheetContent>
        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="primary" onClick={notesVM.movedNotice.close}>
              Got it!
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>

      <ConfirmSheet danger yes="Delete note" state={notesVM.confirmDeleteNote}>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>Delete this note?</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            This note will be removed for your whole team. This can't be undone.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ConfirmSheet>
    </>
  )
})
